import { TouchableOpacity, Text, StyleSheet, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { SettingSection } from '@/components/settings/SettingSection';
import { Spacing } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

interface DangerZoneSectionProps {
  textColor: string;
  onDeleteAccountPress: () => void;
}

/**
 * DangerZoneSection — destructive account-level actions on the Your Account
 * screen. Currently exposes only "Delete Account", which opens the
 * confirmation modal owned by the parent screen.
 */
export function DangerZoneSection({
  textColor,
  onDeleteAccountPress,
}: DangerZoneSectionProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  return (
    <SettingSection title="DANGER ZONE" textColor={textColor}>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel="Delete account"
        activeOpacity={0.7}
        onPress={onDeleteAccountPress}
        style={styles.row}
      >
        <View style={styles.iconContainer}>
          <Ionicons name="trash-outline" size={22} color={palette.danger} />
        </View>
        <View style={styles.labelContainer}>
          <Text style={[styles.label, { color: palette.danger, fontFamily: getBodyFont() }]}>
            Delete Account
          </Text>
          <Text
            style={[styles.helper, { color: textColor, fontFamily: getBodyFont() }]}
          >
            Permanently deletes your FimFam account and stored data. Your
            calendar events remain in your native calendar.
          </Text>
        </View>
      </TouchableOpacity>
    </SettingSection>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    paddingVertical: Spacing.md + Spacing.xxs,
    paddingHorizontal: Spacing.lg,
    alignItems: 'flex-start',
    minHeight: 60,
  },
  iconContainer: {
    width: 30,
    marginRight: Spacing.md,
    alignItems: 'center',
    paddingTop: 2,
  },
  labelContainer: {
    flex: 1,
    gap: 2,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
  },
  helper: {
    fontSize: 12,
    opacity: 0.7,
  },
});
